import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { StatusCell } from '@/components/molecules/StatusCell';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { Account } from '@/types/contacts';
import { PaymentRailType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Locale } from '@/types/locale';
import { FeeType, simplifyTransactionStatus, SpreadFee, Transaction } from '@/types/transaction';
import { TransactionRow } from '@/types/transactionRow';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';
import { transactionFromToAmount } from '@/utility/transactionFromToAmount';

interface Props {
  readonly transaction: Transaction;
  readonly field: keyof TransactionRow;
}

const findIntegratorSpread = (transaction: Transaction): SpreadFee | undefined =>
  transaction.transactionDetails?.feeDetails?.spreadFees?.find(
    (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
  );

const emptyPaymentTypes = [FiatAccountType.unset, PaymentRailType.LocalExchange];

export const TransactionCellContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { transaction, field } = props;
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const dateFormatter = useDateFormatter('MMM d, yyyy');
  const shouldShowPendingACAM220FileAlert = transaction.efx && !transaction.hasAcam220File;

  switch (field) {
    case 'date':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {dateFormatter.parseAndFormat(transaction.date)}
        </Typography>
      );
    case 'type':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {tr(transaction.type)}
        </Typography>
      );
    case 'paymentType':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {emptyPaymentTypes.includes(transaction.paymentType) ? '--' : tr(transaction.paymentType)}
        </Typography>
      );
    case 'fromAmount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transactionFromToAmount(locale, transaction.fromAmount, transaction.toAmount)}
        </Typography>
      );
    case 'fromAccount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transaction.fromAccount ? tr(accountToString(transaction.fromAccount)) : ''}
        </Typography>
      );
    case 'toAccount':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {transaction.toRecipient
            ? tr(
                transaction.toRecipient.individualName ??
                  transaction.toRecipient.businessName ??
                  '',
              )
            : transaction.toAccount
              ? tr(accountToString(transaction.toAccount))
              : ''}
        </Typography>
      );
    case 'status':
      return (
        <Box sx={styles.statusCell}>
          <StatusCell
            status={simplifyTransactionStatus(transaction.status, transaction.completed)}
          />
          {shouldShowPendingACAM220FileAlert && (
            <Tooltip
              title={
                <Typography>
                  {tr('PENDING_ACAM_220_FILE_ALERT')}{' '}
                  <Typography component="strong" fontWeight={600}>
                    {tr('PENDING_ACAM_220_FILE_ALERT_TRANSACTIONS_LIST_ACTION')}
                  </Typography>
                </Typography>
              }>
              <Box sx={styles.infoIcon}>
                <InfoIcon fill="currentColor" width={24} height={24} />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    case 'markup': {
      const spread = findIntegratorSpread(transaction);
      const bps = spread ? Number(spread.spreadBasisPoint) * 10000 : null;
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {bps !== null ? `${bps} bps` : '--'}
        </Typography>
      );
    }
    case 'earned': {
      const spread = findIntegratorSpread(transaction);
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {spread && Number(spread.calculatedFee) > 0
            ? formatAmount(
                locale,
                Number(spread.calculatedFee),
                spread.feeCurrency,
                FormatStyle.short,
              )
            : '--'}
        </Typography>
      );
    }
    default:
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {String(transaction[field as keyof Transaction])}
        </Typography>
      );
  }
};

const styles: Record<string, SystemStyleObject> = {
  cell: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  infoIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'warning.main',
  },
};

const accountToString = (account: Account): string => {
  return account.name;
};
